import {AjaxDataTypes} from '../../../../shared/AjaxDataTypes'
import {ExistingFriendInterface, FriendOnlineStatus, getOnlineStatusActivityText, OnlineStatusActivityEnum} from '../../../../shared/friends/FriendOnlineStatus'
import {RelativeTimeFormatUnit, TranslationObject} from '@colonist/i18n'
import {UIAjaxWrapper} from '../../../shared/scripts/network/UIAjaxWrapper'
import {UIAPIEndpoints} from '../../../shared/scripts/network/UIAPIEndpoints'
import {ViewPopupLogin} from '../ui_popup/ui_popup_membership_incentive/ViewPopupLogin'
import {LobbyImage} from '@colonist/types'
import {AnalyticEventCategory, AnalyticEventType, GiftMembershipMethod} from '../../../../shared/entity_datas/EntityDatas'
import {CA} from '../analytics/Analytics'
import {doActionIfCanGiftMembership} from '../store/StoreUtils'
import {UIDeviceController} from '../ui_html/UIDeviceController'
import {createButtonIn, createDivIn, createImgIn, createLinkIn, createTDIn, createTRIn} from '../ui_utils/DomUtils'
import {ClientUserState} from '../ClientUserState'
import {getProfileURI} from '../ui_utils/TestableClientUtils'
import {TimeUtils} from '@colonist/utils'
import {UIProfileFriendsHorizontalTabsController} from './UIProfileFriendsHorizontalTabsController'
import {navigateToPage} from '../ui_utils/ClientUtils'
import {UIUtilsFriends} from '../ui_friends/UIUtilsFriends'
import {UIUtilsEjsVariables} from '../UIUtilsEjsVariables.ts'

function createLoginPrompt(container: HTMLElement) {
    const text: TranslationObject = {key: 'strings:profilePage.friends.loginToAddFriends'}
    const loginLink = createLinkIn(container, '', text, '')
    loginLink.onpointerdown = (event: MouseEvent) => {
        event.preventDefault()
        new ViewPopupLogin()
    }
}

export class UIProfileFriendsTableController {
    protected readonly userFriendsData: AjaxDataTypes.UserFriendsData
    protected readonly friendsTableBody: HTMLTableSectionElement
    protected readonly friendRequestsTableBody: HTMLTableSectionElement
    protected readonly requestsSentTableBody: HTMLTableSectionElement
    protected readonly friendsTableBodyWrapper: HTMLDivElement
    protected readonly friendRequestsTableBodyWrapper: HTMLDivElement
    protected readonly requestsSentTableBodyWrapper: HTMLDivElement
    protected readonly noFriendsDisplay: HTMLDivElement
    protected readonly noFriendRequestsDisplay: HTMLDivElement
    protected readonly noRequestsSentDisplay: HTMLDivElement
    private readonly horizontalTabsController: UIProfileFriendsHorizontalTabsController

    constructor(userFriendsData: AjaxDataTypes.UserFriendsData) {
        this.userFriendsData = userFriendsData
        this.friendsTableBody = document.getElementById('friendspage_friends_table_body') as HTMLTableSectionElement
        this.friendRequestsTableBody = document.getElementById('friendspage_friendrequests_table_body') as HTMLTableSectionElement
        this.requestsSentTableBody = document.getElementById('friendspage_requestssent_table_body') as HTMLTableSectionElement
        this.friendsTableBodyWrapper = document.getElementById('friendspage_friends_table_body_wrapper') as HTMLDivElement
        this.friendRequestsTableBodyWrapper = document.getElementById('friendspage_friendrequests_table_body_wrapper') as HTMLDivElement
        this.requestsSentTableBodyWrapper = document.getElementById('friendspage_requestssent_table_body_wrapper') as HTMLDivElement
        this.noFriendsDisplay = document.getElementById('no_friends_display') as HTMLDivElement
        if(!ClientUserState.userState?.isLoggedIn) createLoginPrompt(this.noFriendsDisplay)
        this.noFriendRequestsDisplay = document.getElementById('no_friendspage_friendrequests_display') as HTMLDivElement
        this.noRequestsSentDisplay = document.getElementById('no_friendspage_requestssent_display') as HTMLDivElement

        this.horizontalTabsController = new UIProfileFriendsHorizontalTabsController(this.userFriendsData)
        this.createFriendsTable()
        this.createFriendRequestTable()
        this.createRequestsSentTable()

        void this.updateSuggestedFriendsVisibility()
        this.updateFriendRequestTableVisibility()
        this.updateSentRequestTableVisibility()
        UIUtilsFriends.setCallbackFriendRequestResponded((username: string, isAccepted: boolean) => this.deleteFriendRequestTableRow(username, isAccepted))
    }

    private async updateSuggestedFriendsVisibility() {
        const hasFriends = this.userFriendsData.friends.length > 0
        if(hasFriends) {
            this.noFriendsDisplay.classList.add('hidden')
            this.friendsTableBodyWrapper.classList.remove('hidden')
            return
        }
        this.noFriendsDisplay.classList.remove('hidden')
        this.friendsTableBodyWrapper.classList.add('hidden')
    }

    private updateFriendRequestTableVisibility() {
        const hasFriendRequests = this.userFriendsData.friendRequestsReceived.length > 0
        if(hasFriendRequests) {
            this.noFriendRequestsDisplay.classList.add('hidden')
            this.friendRequestsTableBodyWrapper.classList.remove('hidden')
            return
        }
        this.noFriendRequestsDisplay.classList.remove('hidden')
        this.friendRequestsTableBodyWrapper.classList.add('hidden')
    }

    private updateSentRequestTableVisibility() {
        const hasSentRequests = this.userFriendsData.friendRequestsSent.length > 0
        if(hasSentRequests) {
            this.noRequestsSentDisplay.classList.add('hidden')
            this.requestsSentTableBodyWrapper.classList.remove('hidden')
            return
        }
        this.noRequestsSentDisplay.classList.remove('hidden')
        this.requestsSentTableBodyWrapper.classList.add('hidden')
    }

    private static getFriendLastOnlineTime(friendOnlineStatus: FriendOnlineStatus): number {
        if(friendOnlineStatus.isOnline) return new Date().getTime()
        if(friendOnlineStatus.lastOnlineDate === undefined) return 0
        return new Date(friendOnlineStatus.lastOnlineDate).getTime()
    }

    private createFriendsTable() {
        const usersOrderedByLastOnline = this.userFriendsData.friends.sort((firstUser: ExistingFriendInterface, secondUser: ExistingFriendInterface) => {
            const firstUserLastOnlineTime = UIProfileFriendsTableController.getFriendLastOnlineTime(firstUser.onlineStatus)
            const secondUserLastOnlineTime = UIProfileFriendsTableController.getFriendLastOnlineTime(secondUser.onlineStatus)
            if(secondUserLastOnlineTime > firstUserLastOnlineTime) return 1
            return -1
        })
        usersOrderedByLastOnline.forEach((friend: ExistingFriendInterface) => {
            this.createFriendTableRow(friend)
        })
    }

    private createFriendRequestTable() {
        this.userFriendsData.friendRequestsReceived.forEach((friendRequest: string) => {
            this.createFriendRequestTableRow(friendRequest)
        })
    }

    private createRequestsSentTable() {
        this.userFriendsData.friendRequestsSent.forEach((requestSent: string) => {
            this.createRequestSentTableRow(requestSent)
        })
    }

    private async deleteFriendRequestTableRow(friendRequestUsername: string, isAccepted: boolean) {
        const indexToDelete = this.userFriendsData.friendRequestsReceived.findIndex((username) => username == friendRequestUsername)
        this.friendRequestsTableBody.deleteRow(indexToDelete)
        this.userFriendsData.friendRequestsReceived.splice(indexToDelete, 1)

        if(isAccepted) {
            const acceptedFriendData = {
                username: friendRequestUsername,
                onlineStatus: {isOnline: false},
            }
            this.createFriendTableRow(acceptedFriendData)
            this.userFriendsData.friends.push(acceptedFriendData)
        }

        this.updateFriendRequestTableVisibility()
        await this.updateSuggestedFriendsVisibility()
        this.horizontalTabsController.updateCountInTabHeader(this.userFriendsData)
    }

    protected async cancelFriendRequestAction(cancelFriendRequestUsername: string) {
        const friendChangeData: AjaxDataTypes.FriendChangeData = {username: cancelFriendRequestUsername}
        const response = await UIAjaxWrapper.postRequestHandler(UIAPIEndpoints.apiUserProfileRemoveFriend(), undefined, friendChangeData, 'application/json')
        if(response == undefined) return

        const indexToDelete = this.userFriendsData.friendRequestsSent.findIndex((requestSentUsername) => requestSentUsername == cancelFriendRequestUsername)
        this.requestsSentTableBody.deleteRow(indexToDelete)
        this.userFriendsData.friendRequestsSent.splice(indexToDelete, 1)

        this.updateSentRequestTableVisibility()
        this.horizontalTabsController.updateCountInTabHeader(this.userFriendsData)
    }

    protected createFriendTableRow(existingFriend: ExistingFriendInterface): void {
        const friendOnlineStatus = existingFriend.onlineStatus
        const row = createTRIn(this.friendsTableBody, '')
        row.onclick = () => {
            if(UIDeviceController.isUserOnMobileApp()) return
            navigateToPage(getProfileURI(existingFriend.username), true)
        }
        const usernameTD = createTDIn(row, 'underline_on_hover ellipsis', {key: 'strings:colonistCommon.utils.#buffer', options: {value: existingFriend.username}})
        const statusTD = createTDIn(row, undefined, UIProfileFriendsTableController.getStatusText(friendOnlineStatus))
        const activityTD = createTDIn(row, undefined, getOnlineStatusActivityText(friendOnlineStatus.activity))
        const actionTD = createTDIn(row)

        if(!UIUtilsEjsVariables.isUserOnDiscordMobile) UIProfileFriendsTableController.createGiftButtonIn(actionTD, existingFriend.username)
        UIProfileFriendsTableController.setActionHTML(friendOnlineStatus, actionTD)
    }

    private static createGiftButtonIn(container: HTMLElement, friendUsername: string) {
        const giftBtn = createDivIn(container, 'btn-general-icon')
        createImgIn(giftBtn, LobbyImage.StoreGiftMembership)
        const giftButtonClickEventData: AjaxDataTypes.AnalyticData = {
            type: AnalyticEventType.ABTestClickAnalytics,
            category: AnalyticEventCategory.GiftMembershipOverlay,
            floatValue: GiftMembershipMethod.ProfileFriendsTab,
        }
        giftBtn.onclick = (event) => {
            event.stopPropagation()
            void CA.sendAnalyticEvent(giftButtonClickEventData)
            doActionIfCanGiftMembership(() => navigateToPage(`/store?username=${friendUsername}#gift`, true), friendUsername)
        }
    }

    createFriendRequestTableRow(friendRequestUsername: string): void {
        const row = createTRIn(this.friendRequestsTableBody, friendRequestUsername.toLowerCase())
        row.onclick = () => {
            if(UIDeviceController.isUserOnMobileApp()) return
            navigateToPage(getProfileURI(friendRequestUsername), true)
        }
        const usernameTD = createTDIn(row, 'underline_on_hover', {key: 'strings:colonistCommon.utils.#buffer', options: {value: friendRequestUsername}})
        const actionTD = createTDIn(row)
        const actionsContainer = createDivIn(actionTD, '')
        const declineButton = createButtonIn(actionsContainer, 'friendspage_friends_decline_btn', {key: 'strings:colonistCommon.buttons.decline'})
        declineButton.onclick = (event: MouseEvent) => {
            event.stopPropagation()
            void UIUtilsFriends.friendRequestResponded(friendRequestUsername, false)
        }
        const acceptButton = createButtonIn(actionsContainer, 'friendspage_friends_accept_btn', {key: 'strings:colonistCommon.buttons.accept'})
        acceptButton.onclick = (event: MouseEvent) => {
            event.stopPropagation()
            void UIUtilsFriends.friendRequestResponded(friendRequestUsername, true)
        }
    }
    createRequestSentTableRow(username: string) {
        const row = createTRIn(this.requestsSentTableBody, '')
        row.onclick = () => {
            if(UIDeviceController.isUserOnMobileApp()) return
            navigateToPage(getProfileURI(username), true)
        }
        createTDIn(row, 'underline_on_hover', {key: 'strings:colonistCommon.utils.#buffer', options: {value: username}})
        const actionTD = createTDIn(row)
        const actionsContainer = createDivIn(actionTD, '')
        const cancelButton = createButtonIn(actionsContainer, 'friendspage_friends_decline_btn', {key: 'strings:colonistCommon.buttons.cancel'})
        cancelButton.onclick = (event: MouseEvent) => {
            event.stopPropagation()
            void this.cancelFriendRequestAction(username)
        }
    }

    private static getStatusText(friendOnlineStatus: FriendOnlineStatus): TranslationObject {
        if(friendOnlineStatus.isOnline) return {key: 'strings:profilePage.friends.status.online'}
        if(!friendOnlineStatus.lastOnlineDate) return {key: 'strings:profilePage.friends.status.offline'}
        const deltaDaysHoursMinutes = TimeUtils.getDaysHoursMinutesBetweenDates(new Date(), new Date(friendOnlineStatus.lastOnlineDate))
        if(deltaDaysHoursMinutes.days > TimeUtils.DAYS_IN_MONTH * 4) return {key: 'strings:profilePage.friends.status.lastOnlineMoreThan3Months'}
        if(deltaDaysHoursMinutes.days > TimeUtils.DAYS_IN_MONTH) {
            const months = Math.floor(deltaDaysHoursMinutes.days / TimeUtils.DAYS_IN_MONTH)
            return UIProfileFriendsTableController.getLastOnlineText(months, 'month')
        }
        if(deltaDaysHoursMinutes.days > 0) return UIProfileFriendsTableController.getLastOnlineText(deltaDaysHoursMinutes.days, 'day')
        if(deltaDaysHoursMinutes.hours > 0) return UIProfileFriendsTableController.getLastOnlineText(deltaDaysHoursMinutes.hours, 'hour')
        return UIProfileFriendsTableController.getLastOnlineText(deltaDaysHoursMinutes.minutes, 'minute')
    }

    private static setActionHTML(friendOnlineStatus: FriendOnlineStatus, actionTD: HTMLTableCellElement) {
        if(friendOnlineStatus.isOnline == false) return
        switch(friendOnlineStatus.activity) {
            case OnlineStatusActivityEnum.InPublicRoom:
                UIProfileFriendsTableController.createJoinButton(friendOnlineStatus, actionTD)
                break
            case OnlineStatusActivityEnum.PlayingCitiesAndKnights4P:
            case OnlineStatusActivityEnum.PlayingCitiesAndKnights56P:
            case OnlineStatusActivityEnum.PlayingCitiesAndKnightsSeafarers4P:
            case OnlineStatusActivityEnum.PlayingCitiesAndKnightsSeafarers56P:
            case OnlineStatusActivityEnum.PlayingClassic4P:
            case OnlineStatusActivityEnum.PlayingClassic5678P:
            case OnlineStatusActivityEnum.PlayingSeafarers4P:
            case OnlineStatusActivityEnum.PlayingSeafarers56P:
                UIProfileFriendsTableController.createSpectateButton(friendOnlineStatus, actionTD)
                break
            case OnlineStatusActivityEnum.InPrivateRoom:
            case OnlineStatusActivityEnum.IsOnlineAndIdle:
            default:
                return
        }
    }

    private static createSpectateButton(friendOnlineStatus: FriendOnlineStatus, actionTD: HTMLTableCellElement) {
        const spectateButton = createButtonIn(actionTD, 'btn_general', {key: 'strings:homePage.ctas.spectate'})
        spectateButton.onclick = () => UIProfileFriendsTableController.joinFriendGameAction(friendOnlineStatus)
    }

    private static createJoinButton(friendOnlineStatus: FriendOnlineStatus, actionTD: HTMLTableCellElement) {
        const joinButton = createButtonIn(actionTD, 'btn_general', {key: 'strings:homePage.ctas.joinGame'})
        joinButton.onclick = () => UIProfileFriendsTableController.joinFriendGameAction(friendOnlineStatus)
    }

    private static joinFriendGameAction(friendOnlineStatus: FriendOnlineStatus) {
        if(friendOnlineStatus.roomId != undefined) {
            navigateToPage(`/#${friendOnlineStatus.roomId}`)
        }
    }

    private static getLastOnlineText(count: number, unit: RelativeTimeFormatUnit): TranslationObject {
        return {
            key: 'strings:profilePage.friends.status.lastOnline',
            options: {
                context: unit,
                count: -count,
            },
        }
    }
}
